<template>
  <b-container class="px-0 autocomplete-container" @keydown.enter.prevent="enterPressed">
    <b-input-group>
      <b-input 
        ref="input" 
        v-model="searchQuery" 
        @focus="onInputFocus"
        @input="onInputChange"
        @keydown.down.prevent="navigateDown"
        @keydown.up.prevent="navigateUp"
        placeholder="Voer een ingredient in" 
        autocomplete="off"
        :aria-expanded="dropdownVisible"
        aria-autocomplete="list"
        role="combobox"
      />
      <template #append>
        <b-button variant="primary" @click="onClearInput">
          <b-icon icon="x-circle" style="color: #FFFFFF;"/>
        </b-button>
      </template>
    </b-input-group>
    
    <!-- Fixed positioned dropdown voor betere iOS support -->
    <div 
      v-show="dropdownVisible" 
      class="autocomplete-dropdown-portal"
      :style="dropdownPortalStyle"
    >
      <ul class="autocomplete-list" role="listbox">
        <li 
          v-for="(item, index) in filteredItems" 
          :key="index"
          :class="['autocomplete-item', { 
            'active': highlightedIndex === index,
            'disabled': item.disabled 
          }]"
          @click="selectItem(item)"
          @touchstart="onItemTouchStart"
          @mouseenter="highlightedIndex = index"
          role="option"
          :aria-selected="highlightedIndex === index"
        >
          {{ item[textField] }}
        </li>
      </ul>
    </div>
  </b-container>
</template>

<script>
export default {
  name: "autocomplete",
  props: ["items", "textField", "valueField", "selItem"],
  data() {
    return {
      searchQuery: "",
      dropdownVisible: false,
      filteredItems: [],
      highlightedIndex: 0,
      touchStarted: false,
      selectedValue: null,
      dropdownPortalStyle: {}
    }
  },
  watch: {
    selItem: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal[this.textField]) {
          this.searchQuery = newVal[this.textField];
        }
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    onInputFocus() {
      if (this.searchQuery && this.searchQuery.length > 0) {
        this.filterItems();
        this.showDropdown();
      }
    },
    
    onInputChange() {
      this.filterItems();
      if (this.filteredItems.length > 0 && this.searchQuery.length > 0) {
        this.showDropdown();
        this.highlightedIndex = 0;
      } else {
        this.hideDropdown();
      }
    },
    
    filterItems() {
      if (!this.searchQuery || this.searchQuery === "") {
        this.filteredItems = [];
        return;
      }
      
      if (!this.items || !Array.isArray(this.items)) {
        this.filteredItems = [];
        return;
      }
      
      const searchTerms = this.searchQuery.toLowerCase().split(" ");
      
      const filtered = this.items.filter(item => {
        if (!item || !item[this.textField]) {
          return false;
        }
        const itemText = item[this.textField].toLowerCase();
        return searchTerms.every(term => itemText.includes(term));
      });
      
      // Toon alleen echte resultaten, geen placeholder-item
      this.filteredItems = filtered;
    },
    
    showDropdown() {
      this.updateDropdownPosition();
      this.dropdownVisible = true;
    },
    
    updateDropdownPosition() {
      if (this.$refs.input && this.$refs.input.$el) {
        const inputEl = this.$refs.input.$el;
        const rect = inputEl.getBoundingClientRect();
        this.dropdownPortalStyle = {
          position: 'fixed',
          top: `${rect.bottom + 2}px`,
          left: `${rect.left}px`,
          width: `${rect.width}px`,
          zIndex: 99999
        };
      }
    },
    
    hideDropdown() {
      setTimeout(() => {
        this.dropdownVisible = false;
      }, 150);
    },
    
    navigateDown() {
      if (!this.dropdownVisible && this.searchQuery) {
        this.filterItems();
        this.showDropdown();
        return;
      }
      
      if (this.highlightedIndex < this.filteredItems.length - 1) {
        this.highlightedIndex++;
        if (this.filteredItems[this.highlightedIndex].disabled) {
          this.navigateDown();
        }
      }
    },
    
    navigateUp() {
      if (this.highlightedIndex > 0) {
        this.highlightedIndex--;
        if (this.filteredItems[this.highlightedIndex].disabled) {
          this.navigateUp();
        }
      }
    },
    
    enterPressed() {
      if (this.dropdownVisible && this.filteredItems.length > 0) {
        const item = this.filteredItems[this.highlightedIndex];
        if (!item.disabled) {
          this.selectItem(item);
        }
      }
    },
    
    onItemTouchStart() {
      this.touchStarted = true;
    },
    
    selectItem(item) {
      if (item.disabled) return;
      
      if (this.touchStarted) {
        this.touchStarted = false;
        setTimeout(() => this.executeSelection(item), 50);
      } else {
        this.executeSelection(item);
      }
    },
    
    executeSelection(item) {
      this.selectedValue = item[this.valueField];
      this.searchQuery = item[this.textField];
      this.hideDropdown();
      this.$emit("itemSelected", item[this.valueField]);
    },
    
    onClearInput() {
      this.searchQuery = "";
      this.selectedValue = null;
      this.filteredItems = [];
      this.hideDropdown();
    },
    
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.hideDropdown();
      }
    }
  }
}
</script>

<style scoped>
.autocomplete-container {
  position: relative;
}

.autocomplete-dropdown-portal {
  background-color: white;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  box-shadow: 0 4px 6px rgba(0,0,0,0.15);
  max-height: 200px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  -webkit-transform: translate3d(0,0,0);
  transform: translate3d(0,0,0);
}

.autocomplete-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.autocomplete-item {
  padding: 0.5rem 1rem;
  cursor: pointer;
  transition: background-color 0.15s ease;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.autocomplete-item:hover:not(.disabled),
.autocomplete-item.active:not(.disabled) {
  background-color: #f8f9fa;
}

.autocomplete-item.disabled {
  color: #6c757d;
  cursor: default;
  font-style: italic;
}

@media (max-width: 768px) {
  .autocomplete-item {
    padding: 0.75rem 1rem;
    font-size: 16px;
  }
}

@media (prefers-contrast: high) {
  .autocomplete-item.active:not(.disabled) {
    outline: 2px solid;
  }
}
</style>